import { useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import type { PersonalLaptopConfig } from "../scenario/types";
import { audio } from "../lib/audio";
import { Spotlight } from "../ui/Spotlight";
import { useSim } from "../state/store";
import { PdfBriefing } from "../ui/PdfBriefing";
import { tx, txr } from "../lib/tx";

/**
 * Episode 10 — "the personal laptop". A single guided device (mirrors the
 * ByodLaptop/PublicPc pattern: one config, many internal stages, a linear
 * story rather than a branching decision) that tells the WHOLE causal chain
 * for the ransomware episode that follows, instead of the screen just
 * starting to flicker with no explanation:
 *
 *   power on -> connect the VPN (system tray icon, spotlight-guided) ->
 *   VPN grants access to internal file shares -> open the browser -> search
 *   "popular browser games" -> click the (fake) top result -> a fake game
 *   site pushes a "required game launcher" download -> a Yes/No "is this
 *   safe?" interstitial (recorded as a learning signal, never a gate — see
 *   `answerDownloadQuiz`) -> install -> the game actually plays for a few
 *   seconds -> the screen flickers, cascading Windows errors appear, then
 *   goes black -> onComplete() hands off to the existing b_ransom_takeover
 *   cinematic.
 *
 * The "walk to the lounge and click the laptop" framing reuses the SAME
 * Desk A laptop already modelled for Episode 3 (see
 * three/PrivacyRoomProps.tsx's `Laptop`, `stickyCaption` prop — there is no
 * second/duplicate laptop prop) with a clickable sticky note on the bezel,
 * projected to screen space every frame; `personallaptop` is a
 * SCREEN_KINDS member in App.tsx exactly like desktop/mail/browser/etc, so
 * this component only ever MOUNTS after the learner has clicked that note
 * (App's power-on gate, keyed off the laptop's own `loungePin`, flips the
 * shared `screenOn` flag) — no internal "off"/lid-click stage needed here,
 * it powers straight on. Every stage below fills the space that lines up on
 * the 3D screen's LCD area — see wp_lounge_laptop's dead-on camera pose and
 * the `.plt-*` sizing in styles.css.
 */

type Stage =
  | "desktop"
  | "briefing_desktop"
  | "vpn_popup"
  | "vpn_connecting"
  | "vpn_connected"
  | "browser_home"
  | "results"
  | "fake_site"
  | "download_quiz"
  | "downloading"
  | "installing"
  | "game"
  | "stealer"
  | "flicker"
  | "black";

const DEFAULT_RESOURCES = ["Windows file servers", "SMB shares", "Internal NAS"];
const DEFAULT_RESULTS: NonNullable<PersonalLaptopConfig["searchResults"]> = [
  {
    title: "Best browser games to play in your lunch break \u2014 Wired",
    url: "www.wired.com/browser-games-2026",
    snippet: "No install needed \u2014 our picks for the best games you can play straight from Chrome...",
  },
  {
    title: "PlayArcade Unblocked \u2014 Free Retro Games, No Download Needed!",
    url: "www.playarcade-unblocked.net",
    snippet: "Retro Space Invaders, Snake, Pac-Man and 100+ classics. Play instantly in your browser, 100% free.",
    fake: true,
  },
  {
    title: "r/browsergames \u2014 Reddit",
    url: "www.reddit.com/r/browsergames",
    snippet: "What's everyone playing this week? Looking for something to kill 10 minutes...",
  },
];

type InvaderCell = { id: number; row: number; col: number; alive: boolean; exploding: boolean };

const RETRO_ROWS = 3;
const RETRO_COLS = 6;

function buildInvaderGrid(): InvaderCell[] {
  const grid: InvaderCell[] = [];
  let id = 0;
  for (let row = 0; row < RETRO_ROWS; row++) {
    for (let col = 0; col < RETRO_COLS; col++) {
      grid.push({ id: id++, row, col, alive: true, exploding: false });
    }
  }
  return grid;
}

/**
 * The fake "game launcher" that opens right after the malicious download —
 * a real-time (if simple) mini Space Invaders so the moment reads as "the
 * user is actually playing something", not a static screenshot. Fully
 * auto-playing (no learner input needed — this is a short handoff cutscene
 * before the flicker/crash sequence, not an interactive minigame) but drives
 * genuine per-frame movement + collision feedback: the formation drifts
 * left/right, a laser fires at a random surviving invader on a beat, and a
 * hit triggers a brief explosion sprite before the invader is removed. CRT
 * scanlines + a green phosphor glow replace the old plain emoji rows.
 */
function RetroInvadersGame({ active, gameName }: { active: boolean; gameName: string }) {
  const [grid, setGrid] = useState<InvaderCell[]>(() => buildInvaderGrid());
  const [formationX, setFormationX] = useState(0);
  const [shot, setShot] = useState<{ col: number; row: number } | null>(null);
  const [playerCol, setPlayerCol] = useState(Math.floor(RETRO_COLS / 2));
  const [score, setScore] = useState(0);
  const dirRef = useRef(1);

  useEffect(() => {
    if (!active) return;
    setGrid(buildInvaderGrid());
    setFormationX(0);
    setShot(null);
    setPlayerCol(Math.floor(RETRO_COLS / 2));
    setScore(0);
    dirRef.current = 1;

    const driftIv = window.setInterval(() => {
      setFormationX((x) => {
        const next = x + dirRef.current * 14;
        if (next > 30 || next < -30) dirRef.current *= -1;
        return Math.max(-30, Math.min(30, next));
      });
    }, 420);

    const fireIv = window.setInterval(() => {
      setGrid((prev) => {
        const alive = prev.filter((cell) => cell.alive && !cell.exploding);
        if (alive.length === 0) return prev;
        const target = alive[Math.floor(Math.random() * alive.length)];
        // Move under the selected invader first, then fire from that exact
        // column. Previously the laser jumped around while the shuttle stayed
        // fixed in the centre, so the two animations visibly disagreed.
        setPlayerCol(target.col);
        window.setTimeout(() => {
          audio.retroShoot();
          setShot({ col: target.col, row: target.row });
          window.setTimeout(() => {
            audio.retroHit();
            setScore((s) => s + 10);
            setGrid((cur) => cur.map((cell) => (cell.id === target.id ? { ...cell, exploding: true } : cell)));
            window.setTimeout(() => {
              setGrid((cur) => cur.map((cell) => (cell.id === target.id ? { ...cell, alive: false, exploding: false } : cell)));
              setShot(null);
            }, 240);
          }, 220);
        }, 170);
        return prev;
      });
    // One shot lifecycle is ~630ms (move, fire, hit, clear). Leave headroom
    // before selecting the next column so the shuttle never moves away while
    // the previous laser is still on screen.
    }, 760);

    return () => {
      window.clearInterval(driftIv);
      window.clearInterval(fireIv);
    };
  }, [active]);

  const rows = useMemo(() => {
    const byRow: InvaderCell[][] = Array.from({ length: RETRO_ROWS }, () => []);
    grid.forEach((cell) => byRow[cell.row].push(cell));
    return byRow;
  }, [grid]);

  return (
    <div className="plt-game2" aria-hidden>
      <div className="plt-game2-crt" />
      <div className="plt-game2-bar">
        <span>{txr("{gameName} — Launcher", { gameName })}</span>
        <span className="plt-game2-score">{txr("SCORE {v}", { v: String(score).padStart(5, "0") })}</span>
      </div>
      <div className="plt-game2-stage">
        <div className="plt-game2-formation" style={{ transform: `translateX(${formationX}px)` }}>
          {rows.map((cells, rowIdx) => (
            <div key={rowIdx} className="plt-game2-row">
              {cells.map((cell) => (
                <span key={cell.id} className={`plt-game2-invader ${cell.exploding ? "explode" : ""} ${!cell.alive ? "dead" : ""}`}>
                  {cell.exploding ? "\uD83D\uDCA5" : "\uD83D\uDC7E"}
                </span>
              ))}
            </div>
          ))}
        </div>
        {shot && (
          <div
            className="plt-game2-shot"
            style={
              {
                left: `${((shot.col + 0.5) / RETRO_COLS) * 100}%`,
                "--shot-target": `${34 + (RETRO_ROWS - shot.row) * 15}%`,
              } as CSSProperties
            }
          />
        )}
        <div
          className="plt-game2-player"
          style={{ left: `${((playerCol + 0.5) / RETRO_COLS) * 100}%` }}
        >
          {"\uD83D\uDE80"}
        </div>
      </div>
    </div>
  );
}

const STEALER_LOGS = [
  ["INIT", "browser monitor attached · profile=Personal"],
  ["URL", "shop.arborandco.example / wireless-headphones"],
  ["CREDENTIAL", "email=alex.morgan@example.test"],
  ["CREDENTIAL", "password=Pineapple!2026"],
  ["FORM", "shipping_name=Alex Morgan · postcode=10001"],
  ["PAYMENT", "card=•••• 4242 · expiry=12/29"],
  ["SESSION", "shop_session=eyJhbGciOi... captured"],
  ["EXFIL", "bundle encrypted → collector.onion / received"],
] as const;

/** Infostealer outcome used by the breach course. Left is the normal user
 *  experience; right is an explanatory attacker-only view the victim never
 *  sees. No ransomware theatrics: the purchase succeeds normally. */
function StealerCinematic({ onDone }: { onDone: () => void }) {
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => setPhase((value) => {
      if (value >= 5) {
        window.clearInterval(id);
        return value;
      }
      return value + 1;
    }), 1250);
    return () => window.clearInterval(id);
  }, []);

  const screen =
    phase === 0 ? "home"
      : phase === 1 ? "product"
        : phase === 2 ? "login"
          : phase === 3 ? "checkout"
            : "done";
  const visibleLogs = Math.min(STEALER_LOGS.length, 1 + phase * 2);

  return (
    <div className="plt-stealer">
      <header className="plt-stealer-head">
        <div><b>{tx("WHAT THE USER SEES")}</b><span>{tx("Normal shopping session")}</span></div>
        <div className="plt-stealer-divider">{tx("SAME MOMENT")}</div>
        <div><b>{tx("WHAT ONLY THE ATTACKER SEES")}</b><span>{tx("Invisible stealer console")}</span></div>
      </header>

      <div className="plt-stealer-split">
        <section className="plt-user-view">
          <div className="plt-shop-browser">
            <div className="plt-shop-tabs"><span className="on">{tx("Arbor & Co.")}</span><i>+</i></div>
            <div className="plt-shop-url">{"\uD83D\uDD12"} {tx("shop.arborandco.example")}</div>
            <div className={`plt-shop-page is-${screen}`}>
              {screen === "home" && (
                <div className="plt-shop-home">
                  <span>{tx("ARBOR & CO.")}</span><h2>{txr("Everyday technology,{br}delivered.", { br: <br /> })}</h2>
                  <div className="plt-shop-products"><i /><i /><i /></div>
                </div>
              )}
              {screen === "product" && (
                <div className="plt-shop-product">
                  <div className="plt-headphones" aria-hidden>{"\uD83C\uDFA7"}</div>
                  <div><small>{tx("AUDIO")}</small><h2>{tx("QuietWave Headphones")}</h2><strong>$89.00</strong><button>{tx("Add to basket")}</button></div>
                </div>
              )}
              {screen === "login" && (
                <div className="plt-shop-form">
                  <small>{tx("CHECKOUT")}</small><h2>{tx("Sign in to continue")}</h2>
                  <label>{tx("Email")}<input readOnly value="alex.morgan@example.test" /></label>
                  <label>{tx("Password")}<input readOnly value="Pineapple!2026" type="password" /></label>
                  <button>{tx("Sign in")}</button>
                </div>
              )}
              {screen === "checkout" && (
                <div className="plt-shop-form">
                  <small>{tx("SECURE CHECKOUT")}</small><h2>{tx("Payment")}</h2>
                  <label>{tx("Name on card")}<input readOnly value="Alex Morgan" /></label>
                  <label>{tx("Card number")}<input readOnly value="4242 4242 4242 4242" /></label>
                  <div className="plt-shop-form-row"><input readOnly value="12/29" /><input readOnly value="•••" /></div>
                  <button>{tx("Place order · $89.00")}</button>
                </div>
              )}
              {screen === "done" && (
                <div className="plt-shop-done">
                  <span>{"\u2713"}</span><h2>{tx("Order confirmed")}</h2>
                  <p>{tx("Your QuietWave Headphones are on the way.")}</p>
                  <small>{tx("Nothing looked unusual to the shopper.")}</small>
                </div>
              )}
            </div>
          </div>
        </section>

        <section className="plt-attacker-view">
          <div className="plt-attacker-warning">{tx("ATTACKER VIEW · INVISIBLE ON THE USER'S PC")}</div>
          <div className="plt-terminal-title"><i /><i /><i /><span>{tx("stealer-console — live victim session")}</span></div>
          <div className="plt-terminal-log">
            {STEALER_LOGS.slice(0, visibleLogs).map(([kind, line], index) => (
              <div key={`${kind}-${line}`} className={kind === "EXFIL" ? "is-exfil" : kind === "CREDENTIAL" || kind === "PAYMENT" ? "is-capture" : ""} style={{ animationDelay: `${index * 60}ms` }}>
                <time>{`18:4${Math.min(9, index)}`}</time><b>[{kind}]</b><code>{line}</code>
              </div>
            ))}
            <span className="plt-terminal-caret">_</span>
          </div>
          <div className="plt-stealer-status">
            <span className={phase >= 2 ? "on" : ""}>{tx("Credentials")}</span>
            <span className={phase >= 3 ? "on" : ""}>{tx("Payment form")}</span>
            <span className={phase >= 4 ? "on" : ""}>{tx("Session cookie")}</span>
            <span className={phase >= 5 ? "danger" : ""}>{tx("Exfiltrated")}</span>
          </div>
        </section>
      </div>

      <footer className="plt-stealer-foot">
        <p>{txr("<0>The store worked normally.</0> The stealer copied the data in parallel, without opening a visible terminal for the user.", {}, [<b />])}</p>
        {phase >= 5 && <button type="button" className="btn cf-blink-strong" onClick={onDone}>{tx("See what this means")}</button>}
      </footer>
    </div>
  );
}

function Win11Mark() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden><path d="M3 4.2 10.8 3v8.2H3zm9.2-1.4L21 1.5v9.7h-8.8zM3 12.8h7.8V21L3 19.8zm9.2 0H21v9.7l-8.8-1.3z" /></svg>
  );
}

function EdgeIcon() {
  return (
    <svg viewBox="0 0 32 32" aria-hidden>
      <defs><linearGradient id="plt-edge-g" x1="4" y1="27" x2="27" y2="5"><stop stopColor="#0aa6d8" /><stop offset=".48" stopColor="#0fc7a8" /><stop offset="1" stopColor="#1677d2" /></linearGradient></defs>
      <path fill="url(#plt-edge-g)" d="M28.5 18.2c0 6.4-5.1 10.7-11.7 10.7-7.4 0-13.3-5-13.3-12.4C3.5 8.9 9.2 3 16.9 3c6.2 0 10.5 3.6 11.6 8.5-2.5-3-6.3-4.5-10.1-3.4-4.5 1.2-7.1 5.4-6.1 9.6.8 3.4 3.7 5.6 7.3 5.6 3.8 0 6.8-2 8.9-5.1Z" />
      <path fill="#0877c9" d="M11.9 17.2c1.7-2.4 4.7-3.6 7.7-3.2 4 .5 7.1 2.4 8.9 4.2-2 1.5-5 2.3-8.2 2.1-3.8-.2-6.7-1.3-8.4-3.1Z" />
    </svg>
  );
}

function PdfFileIcon() {
  return (
    <svg viewBox="0 0 32 32" aria-hidden>
      <path fill="#fff" d="M7 2h12l7 7v21H7z" /><path fill="#d9e1e8" d="M19 2v8h7z" />
      <rect x="4" y="15" width="24" height="11" rx="2" fill="#e23d4f" />
      <text x="16" y="23" textAnchor="middle" fill="#fff" fontSize="7" fontWeight="800">{tx("PDF")}</text>
    </svg>
  );
}

function WifiGlyph() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden>
      <path d="M3 9.5c5.2-4.5 12.8-4.5 18 0M6.5 13c3.2-2.8 7.8-2.8 11 0M10 16.5c1.2-1 2.8-1 4 0" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      <circle cx="12" cy="20" r="1.2" fill="currentColor" />
    </svg>
  );
}

export function PersonalLaptop({
  config,
  beatId,
  onComplete,
  onStageChange,
}: {
  config: PersonalLaptopConfig;
  beatId: string;
  /** Passes "safe" when the learner called the download risky: what followed
   *  was only a replay of what would have happened. */
  onComplete: (key?: string) => void;
  onStageChange?: (stage: string) => void;
}) {
  const record = useSim((s) => s.recordOutcome);
  const entryMode = config.entryMode ?? "vpn";
  const [stage, setStageRaw] = useState<Stage>(entryMode === "briefing" ? "briefing_desktop" : "desktop");
  const [vpnOn, setVpnOn] = useState(false);
  const [briefingRead, setBriefingRead] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [typedQuery, setTypedQuery] = useState("");
  // The pre-download safety guess (part g) — a learning signal, not a gate:
  // whichever way the learner answers, the real download/crash sequence
  // always runs afterward (see `startDownload`), so the ransomware episode
  // that follows is never blocked.
  const [downloadGuess, setDownloadGuess] = useState<"safe" | "unsafe" | null>(null);
  const [progress, setProgress] = useState(0);
  const [errorsShown, setErrorsShown] = useState(0);
  const timers = useRef<number[]>([]);
  const spoke = useRef(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const vpnIconRef = useRef<HTMLButtonElement | null>(null);
  const briefingIconRef = useRef<HTMLButtonElement | null>(null);
  const browserIconRef = useRef<HTMLButtonElement | null>(null);

  const appName = config.vpnAppName ?? "OpenVPN";
  const profileName = config.vpnProfileName ?? "Home-VPN";
  const resources = config.vpnResources ?? DEFAULT_RESOURCES;
  const query = config.searchQuery ?? "popular browser games";
  const results = config.searchResults ?? DEFAULT_RESULTS;
  const siteName = config.siteName ?? "PlayArcade Unblocked";
  const gameName = config.gameName ?? "Retro Space Invaders";
  const fileName = config.downloadFileName ?? "GameLauncherSetup.exe";
  const finish = () => onComplete(downloadGuess === "unsafe" ? "safe" : undefined);

  const setStage = (s: Stage) => {
    setStageRaw(s);
    // The desktop is revisited twice with a different task each time (open
    // the VPN, then open the browser) — report a distinct guide key the
    // second time so the tooltip doesn't keep saying "click OpenVPN" once
    // it's already connected.
    onStageChange?.(s === "desktop" && vpnOn ? "desktop_online" : s);
  };

  const after = (ms: number, fn: () => void) => {
    const t = window.setTimeout(fn, ms);
    timers.current.push(t);
    return t;
  };

  // App.tsx's power-on gate already required a real click on the 3D sticky
  // note before this component ever mounts, so it opens straight to the
  // desktop — no lid/power-button stage of its own.
  useEffect(() => {
    audio.powerOn();
    onStageChange?.(entryMode === "briefing" ? "briefing_desktop" : "desktop");
    if (!spoke.current && config.ownerNote) {
      spoke.current = true;
      void audio.speak(config.ownerNote, { voice: "onyx" });
    }
    return () => {
      timers.current.forEach((t) => window.clearTimeout(t));
      audio.cancelSpeech();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openVpnPopup = () => {
    audio.click();
    setStage("vpn_popup");
  };

  const connectVpn = () => {
    audio.click();
    setStage("vpn_connecting");
    after(1300, () => {
      setVpnOn(true);
      setStage("vpn_connected");
    });
  };

  const backToDesktopFromVpn = () => {
    audio.click();
    setStage("desktop");
  };

  function typeSearchQuery() {
    setTypedQuery("");
    let i = 0;
    const iv = window.setInterval(() => {
      i += 1;
      setTypedQuery(query.slice(0, i));
      if (i >= query.length) window.clearInterval(iv);
    }, 45);
  }

  const openBrowser = () => {
    audio.click();
    setStage("browser_home");
    typeSearchQuery();
  };

  const runSearch = () => {
    audio.click();
    setStage("results");
  };

  const openFakeResult = () => {
    audio.click();
    setStage("fake_site");
  };

  const openDownloadQuiz = () => {
    audio.click();
    setDownloadGuess(null);
    setStage("download_quiz");
  };

  const answerDownloadQuiz = (guess: "safe" | "unsafe") => {
    if (guess === "unsafe") audio.chimeGood();
    else audio.chimeBad();
    setDownloadGuess(guess);
    record({
      type: guess === "unsafe" ? "refusal" : "disclosure",
      beatId,
      eventId: guess === "unsafe" ? "recognized_risky_download" : "believed_risky_download_safe",
      evidence:
        guess === "unsafe"
          ? "Guessed the game launcher download was unsafe."
          : "Guessed the game launcher download was safe.",
    });
  };

  const startDownload = () => {
    audio.click();
    setStage("downloading");
    setProgress(0);
    let p = 0;
    const iv = window.setInterval(() => {
      p += 14 + Math.random() * 10;
      setProgress(Math.min(100, p));
      if (p >= 100) {
        window.clearInterval(iv);
        after(300, () => {
          setStage("installing");
          setProgress(0);
          let q = 0;
          const iv2 = window.setInterval(() => {
            q += 16 + Math.random() * 12;
            setProgress(Math.min(100, q));
            if (q >= 100) {
              window.clearInterval(iv2);
              after(350, () => setStage("game"));
            }
          }, 220);
        });
      }
    }, 220);
  };

  // Game plays for a few seconds, then the malware payload triggers.
  useEffect(() => {
    if (stage !== "game") return;
    const t = after(3600, () => {
      audio.retroGameOver();
      setStage(config.outcomeMode === "infostealer" ? "stealer" : "flicker");
    });
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  // Flicker -> cascading Windows errors -> black -> hand off.
  useEffect(() => {
    if (stage !== "flicker") return;
    audio.errorBuzz();
    setErrorsShown(0);
    const t1 = after(500, () => setErrorsShown(1));
    const t2 = after(1150, () => setErrorsShown(2));
    const t3 = after(1750, () => setErrorsShown(3));
    const t4 = after(2700, () => setStage("black"));
    return () => [t1, t2, t3, t4].forEach((t) => window.clearTimeout(t));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  useEffect(() => {
    if (stage !== "black") return;
    const t = after(1300, () => finish());
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  return (
    <>
    <div className="plt-wrap" aria-label={tx("Personal laptop")} ref={wrapRef}>
      <div className="plt-screen">
        {(stage === "desktop" || stage === "briefing_desktop" || stage === "vpn_popup" || stage === "vpn_connecting" || stage === "vpn_connected") && (
          <div className="plt-desktop">
            <div className="plt-desktop-brand">{tx("My Laptop — Personal")}</div>
            <div className="plt-desktop-icons">
              {entryMode === "vpn" && (
                <button
                  ref={vpnIconRef}
                  type="button"
                  className={`plt-dicon plt-dicon--vpn ${vpnOn ? "" : "cf-blink-strong"}`}
                  onClick={openVpnPopup}
                  disabled={stage !== "desktop"}
                >
                  <span className="plt-dicon-glyph plt-dicon-glyph--vpn" aria-hidden><WifiGlyph /></span>
                  <span>{appName}</span>
                </button>
              )}

              {entryMode === "briefing" && (
                <button
                  ref={briefingIconRef}
                  type="button"
                  className={`plt-dicon plt-dicon--briefing ${briefingRead ? "" : "cf-blink-strong"}`}
                  onClick={() => setPdfOpen(true)}
                  disabled={stage !== "briefing_desktop"}
                >
                  <span className="plt-dicon-glyph" aria-hidden><PdfFileIcon /></span>
                  <span>{txr("Download_{br}Safety.pdf", { br: <br /> })}</span>
                </button>
              )}

              {(entryMode === "briefing" || vpnOn) && (
                <button
                  ref={browserIconRef}
                  type="button"
                  className={`plt-dicon plt-dicon--browser ${briefingRead || entryMode === "vpn" ? "cf-blink-strong" : "is-locked"}`}
                  onClick={openBrowser}
                  disabled={
                    (stage !== "desktop" && stage !== "briefing_desktop") ||
                    (entryMode === "briefing" && !briefingRead)
                  }
                >
                  <span className="plt-dicon-glyph" aria-hidden><EdgeIcon /></span>
                  <span>{tx("Microsoft Edge")}</span>
                </button>
              )}
            </div>

            <div className="plt-taskbar">
              <button type="button" className="plt-taskbar-start" aria-label={tx("Start")}><Win11Mark /></button>
              <div className="plt-taskbar-search"><span aria-hidden>{"\u2315"}</span> {tx("Search")}</div>
              <div className="plt-taskbar-pins">
                {entryMode === "briefing" && (
                  <button type="button" title="Download_Safety.pdf" onClick={() => setPdfOpen(true)}>
                    <PdfFileIcon />
                  </button>
                )}
                {entryMode === "vpn" && (
                  <button type="button" title={appName} onClick={openVpnPopup}>
                    <WifiGlyph />
                  </button>
                )}
                <button
                  type="button"
                  title={tx("Microsoft Edge")}
                  onClick={openBrowser}
                  disabled={(entryMode === "briefing" && !briefingRead) || (entryMode === "vpn" && !vpnOn)}
                >
                  <EdgeIcon />
                </button>
              </div>
              <span className="plt-taskbar-spacer" />
              <div className="plt-tray">
                <span className="plt-tray-hidden">^</span>
                <span className="plt-tray-network" title={tx("Network")}><WifiGlyph /></span>
                <span className="plt-tray-volume" aria-hidden>{"\u25B6\uFF09"}</span>
                <span className={`plt-tray-ico ${vpnOn ? "on" : ""}`} aria-hidden>{vpnOn ? "\uD83D\uDD12" : ""}</span>
                <span className="plt-tray-clock"><b>{tx("3:42 PM")}</b><small>9/16/2026</small></span>
              </div>
            </div>

            {/* Half-transparent scrim with a cutout ("spotlight") around whichever
                control is the current target, so it's unambiguous even on a busy
                desktop \u2014 measured live off the real button, so it stays correct
                regardless of how big the laptop's on-screen LCD box ends up being. */}
            <Spotlight
              containerRef={wrapRef}
              targets={[
                entryMode === "briefing"
                  ? briefingRead ? browserIconRef : briefingIconRef
                  : !vpnOn ? vpnIconRef : browserIconRef,
              ]}
              active={stage === "desktop" || stage === "briefing_desktop"}
            />

            {(stage === "vpn_popup" || stage === "vpn_connecting" || stage === "vpn_connected") && (
              <div className="plt-vpn-popup" role="dialog" aria-label={appName}>
                <div className="plt-vpn-head">
                  <span className="plt-vpn-ico" aria-hidden>
                    {"\uD83D\uDD12"}
                  </span>
                  {appName}
                </div>
                {stage === "vpn_popup" && (
                  <div className="plt-vpn-body">
                    <div className="plt-vpn-row">
                      <span className="plt-vpn-dot off" aria-hidden />
                      {tx("Disconnected")}
                    </div>
                    <div className="plt-vpn-profile">{profileName}</div>
                    <button type="button" className="btn plt-vpn-connect cf-blink-strong" onClick={connectVpn}>
                      {tx("Connect")}
                    </button>
                  </div>
                )}
                {stage === "vpn_connecting" && (
                  <div className="plt-vpn-body">
                    <div className="plt-vpn-spinner" aria-hidden />
                    <div className="plt-vpn-connecting-line">{txr("Connecting to {profileName}", { profileName })}…</div>
                  </div>
                )}
                {stage === "vpn_connected" && (
                  <div className="plt-vpn-body">
                    <div className="plt-vpn-row">
                      <span className="plt-vpn-dot on" aria-hidden />
                      {txr("Connected — {profileName}", { profileName })}
                    </div>
                    <div className="plt-vpn-grant-label">{tx("This connection now reaches:")}</div>
                    <ul className="plt-vpn-grants">
                      {resources.map((r) => (
                        <li key={r}>{r}</li>
                      ))}
                    </ul>
                    <button type="button" className="btn cf-blink-strong" onClick={backToDesktopFromVpn}>
                      {tx("Continue")}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {stage === "browser_home" && (
          <div className="plt-browser">
            <div className="plt-browser-bar">
              <span className="plt-browser-lock" aria-hidden>
                {"\uD83D\uDD12"}
              </span>
              <span className="plt-browser-url">google.com</span>
            </div>
            <div className="plt-google">
              <div className="plt-google-logo">{tx("Google")}</div>
              <div className="plt-google-box">
                <span aria-hidden>{"\uD83D\uDD0D"}</span>
                <span className="plt-google-typed">{typedQuery}</span>
                <span className="plt-google-caret" aria-hidden>
                  |
                </span>
              </div>
              {typedQuery === query && (
                <button type="button" className="btn cf-blink-strong" onClick={runSearch}>
                  {tx("Search")}
                </button>
              )}
            </div>
          </div>
        )}

        {stage === "results" && (
          <div className="plt-browser">
            <div className="plt-browser-bar">
              <span className="plt-browser-lock" aria-hidden>
                {"\uD83D\uDD12"}
              </span>
              <span className="plt-browser-url">google.com/search?q={query.replace(/\s+/g, "+")}</span>
            </div>
            <div className="plt-results">
              {results.map((r) =>
                r.fake ? (
                  // The whole row gets a blinking frame (not just the title text)
                  // so it unambiguously reads as "click here", matching the other
                  // blinking hotspots in the course.
                  <button key={r.title} type="button" className="plt-result plt-result--target cf-blink-strong" onClick={openFakeResult}>
                    <div className="plt-result-url">{r.url}</div>
                    <div className="plt-result-title plt-result-title--link">{r.title}</div>
                    <div className="plt-result-snippet">{r.snippet}</div>
                  </button>
                ) : (
                  <div key={r.title} className="plt-result">
                    <div className="plt-result-url">{r.url}</div>
                    <div className="plt-result-title">{r.title}</div>
                    <div className="plt-result-snippet">{r.snippet}</div>
                  </div>
                ),
              )}
            </div>
          </div>
        )}

        {stage === "fake_site" && (
          <div className="plt-browser">
            <div className="plt-browser-bar">
              <span className="plt-browser-lock plt-browser-lock--warn" aria-hidden>
                {"\u26A0"}
              </span>
              <span className="plt-browser-url">playarcade-unblocked.net</span>
            </div>
            <div className="plt-fakesite">
              <div className="plt-fakesite-nav">{siteName}</div>
              <div className="plt-fakesite-ads" aria-hidden>
                <span>{"\uD83C\uDF89"} {tx("YOU WON! CLICK HERE")}</span>
                <span>{"\u2B50"} {tx("Hot singles near you")}</span>
              </div>
              <h1 className="plt-fakesite-title">{gameName}</h1>
              <p className="plt-fakesite-sub">{tx("Play instantly — 100% free, no signup!")}</p>
              <button type="button" className="btn plt-fakesite-cta cf-blink-strong" onClick={openDownloadQuiz}>
                {"\u2B07"} {tx("Download Game Launcher to Play")}
              </button>
              <p className="plt-fakesite-fine">{tx("A small launcher is required to run this game in your browser.")}</p>
            </div>
          </div>
        )}

        {stage === "download_quiz" && (
          <div className="plt-browser">
            <div className="plt-browser-bar">
              <span className="plt-browser-lock plt-browser-lock--warn" aria-hidden>
                {"\u26A0"}
              </span>
              <span className="plt-browser-url">playarcade-unblocked.net</span>
            </div>
            <div className="plt-quiz">
              {downloadGuess === null ? (
                <>
                  <h2 className="plt-quiz-title">{tx("Before you click…")}</h2>
                  <p className="plt-quiz-q">
                    {txr("Is it safe to download and run <0>{fileName}</0> to play this game?", { fileName }, [<strong />])}
                  </p>
                  <div className="plt-quiz-btns">
                    <button type="button" className="btn plt-quiz-btn" onClick={() => answerDownloadQuiz("safe")}>
                      {"\uD83D\uDC4D"} {tx("Yes, it's safe")}
                    </button>
                    <button type="button" className="btn plt-quiz-btn" onClick={() => answerDownloadQuiz("unsafe")}>
                      {"\u26A0"} {tx("No, this is risky")}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <h2 className={`plt-quiz-title ${downloadGuess === "unsafe" ? "good" : "bad"}`}>
                    {downloadGuess === "unsafe" ? tx("✓ Good instinct") : tx("✗ Actually, this is risky")}
                  </h2>
                  <p className="plt-quiz-explain">
                    {tx("A game that needs you to install a separate “launcher” just to play in your browser doesn't add up — real browser games run instantly, no install required. That mismatch is exactly the kind of warning sign worth pausing on.")}
                  </p>
                  <p className="plt-quiz-continue-note">
                    {tx("We'll continue anyway so you can see what would have happened.")}
                  </p>
                  <button type="button" className="btn cf-blink-strong" onClick={startDownload}>
                    {downloadGuess === "unsafe" ? tx("See what would have happened") : tx("Continue to the download")}
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {(stage === "downloading" || stage === "installing") && (
          <div className="plt-browser">
            <div className="plt-installer">
              <div className="plt-installer-file">{fileName}</div>
              <div className="plt-installer-label">
                {stage === "downloading" ? tx("Downloading…") : tx("Installing {gameName} Launcher…", { gameName })}
              </div>
              <div className="plt-progress">
                <div className="plt-progress-fill" style={{ width: `${progress}%` }} />
              </div>
            </div>
          </div>
        )}

        {stage === "game" && <RetroInvadersGame active={stage === "game"} gameName={gameName} />}

        {stage === "stealer" && <StealerCinematic onDone={finish} />}

        {(stage === "flicker" || stage === "black") && (
          <div className={`plt-crash ${stage === "flicker" ? "plt-crash--flicker" : "plt-crash--black"}`}>
            {stage === "black" && <div className="plt-crash-label">{tx("Your files are being encrypted…")}</div>}
            {errorsShown >= 1 && (
              <div className="plt-errdlg plt-errdlg--1">
                <span className="plt-errdlg-ico" aria-hidden>
                  {"\u26A0"}
                </span>
                {tx("Windows Explorer has stopped working")}
              </div>
            )}
            {errorsShown >= 2 && (
              <div className="plt-errdlg plt-errdlg--2">
                <span className="plt-errdlg-ico" aria-hidden>
                  {"\u26A0"}
                </span>
                {tx("Network drive Z:\\ is not responding")}
              </div>
            )}
            {errorsShown >= 3 && (
              <div className="plt-errdlg plt-errdlg--3">
                <span className="plt-errdlg-ico" aria-hidden>
                  {"\u26A0"}
                </span>
                {tx("System service has encountered a problem")}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
    {pdfOpen && config.briefing && (
      <PdfBriefing
        cfg={config.briefing}
        onClose={() => {
          setPdfOpen(false);
          setBriefingRead(true);
          onStageChange?.("briefing_done");
        }}
      />
    )}
    </>
  );
}
